import { useRef } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { Copy, Download, Share2, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "../lib/toast";

interface ShareDialogProps {
  open: boolean;
  title: string;
  description: string;
  url: string;
  onClose(): void;
}

export function ShareDialog({ open, title, description, url, onClose }: ShareDialogProps) {
  const qrRef = useRef<HTMLDivElement>(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast("分享链接已复制", "success");
    } catch {
      toast("浏览器无法复制链接，请手动选择链接", "danger");
    }
  };

  const share = async () => {
    if (!navigator.share) { await copy(); return; }
    try {
      await navigator.share({ title, text: description, url });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      toast("暂时无法打开系统分享面板", "danger");
    }
  };

  const downloadQr = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const source = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = "share-qrcode.svg";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="share-dialog-title" aria-describedby="share-dialog-description">
      <DialogContent className="share-dialog-content">
        <Tooltip title="关闭"><IconButton className="share-dialog-close" type="button" aria-label="关闭分享窗口" onClick={onClose}><X /></IconButton></Tooltip>
        <DialogTitle id="share-dialog-title">{title}</DialogTitle>
        <DialogContentText id="share-dialog-description">{description}</DialogContentText>
        <div className="share-qr" ref={qrRef}>
          <QRCodeSVG value={url} size={288} marginSize={2} level="M" title={`${title}二维码`} />
        </div>
        <TextField className="share-url-field" label="分享链接" value={url} slotProps={{ htmlInput: { readOnly: true } }} onFocus={(event) => (event.target as HTMLInputElement).select()} />
        <DialogActions>
          <Button variant="outlined" type="button" startIcon={<Download />} onClick={downloadQr}>下载二维码</Button>
          <Button variant="outlined" type="button" startIcon={<Copy />} onClick={() => void copy()}>复制链接</Button>
          <Button variant="contained" type="button" startIcon={<Share2 />} onClick={() => void share()}>系统分享</Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
